import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import ServiceDownBanner from "@/components/ServiceDownBanner";
import { AuthProvider } from "@/lib/auth";
import { FeatureProvider } from "@/lib/features";

// Eager (hero path — must render on first paint):
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";

// Lazy (everything else — split into separate chunks):
const Onboarding       = lazy(() => import("@/pages/Onboarding"));
const Dashboard        = lazy(() => import("@/pages/Dashboard"));
const NewEbook         = lazy(() => import("@/pages/NewEbook"));
const Editor           = lazy(() => import("@/pages/Editor"));
const MyBooks          = lazy(() => import("@/pages/MyBooks"));
const Billing          = lazy(() => import("@/pages/Billing"));
const Settings         = lazy(() => import("@/pages/Settings"));
const Usage            = lazy(() => import("@/pages/Usage"));
const Exports          = lazy(() => import("@/pages/Exports"));
const Help             = lazy(() => import("@/pages/Help"));
const MediaLibrary     = lazy(() => import("@/pages/MediaLibrary"));
const ComingSoon       = lazy(() => import("@/pages/ComingSoon"));
const AuthCallback     = lazy(() => import("@/pages/AuthCallback"));
const AITools          = lazy(() => import("@/pages/AITools"));
const Admin            = lazy(() => import("@/pages/Admin"));
const AdminLayout      = lazy(() => import("@/components/AdminLayout"));
const AdminPages       = lazy(() => import("@/pages/admin/AdminPages"));

// Admin page named-export resolver (one chunk for the whole admin area).
const resolveAdmin = (name) => lazy(() =>
  import("@/pages/admin/AdminPages").then((mod) => ({ default: mod[name] }))
);
const AdminOverview      = resolveAdmin("AdminOverview");
const AdminUsers         = resolveAdmin("AdminUsers");
const AdminUserDetail    = resolveAdmin("AdminUserDetail");
const AdminOnline        = resolveAdmin("AdminOnline");
const AdminAnalytics     = resolveAdmin("AdminAnalytics");
const AdminSubscriptions = resolveAdmin("AdminSubscriptions");
const AdminRevenue       = resolveAdmin("AdminRevenue");
const AdminAIUsage       = resolveAdmin("AdminAIUsage");
const AdminAICosts       = lazy(() => import("@/pages/admin/AdminAICosts"));
const Support            = lazy(() => import("@/pages/Support"));
const WhatsAppWidget     = lazy(() => import("@/components/WhatsAppWidget"));
const AdminEbooks        = resolveAdmin("AdminEbooks");
const AdminAudit         = resolveAdmin("AdminAudit");
const AdminErrors        = resolveAdmin("AdminErrors");
const AdminShop          = lazy(() => import("@/pages/admin/AdminShop"));
const AdminShopOrders    = lazy(() => import("@/pages/admin/AdminShopOrders"));

// Public Shop (vetrina — Stripe checkout + WhatsApp delivery)
const Shop         = lazy(() => import("@/pages/Shop"));
const ShopDetail   = lazy(() => import("@/pages/ShopDetail"));
const ShopTerms    = lazy(() => import("@/pages/ShopTerms"));
const ShopSuccess  = lazy(() => import("@/pages/ShopSuccess"));
const VerifyEmail  = lazy(() => import("@/pages/VerifyEmail"));
const MiniUpgrade  = lazy(() => import("@/pages/MiniUpgrade"));

// Protected + CreditsGuard are included only once auth might matter. Keep them
// eager so there's no visual flash on authenticated routes, but they are small.
import Protected from "@/components/Protected";
import CreditsGuard from "@/components/CreditsGuard";
import CookieConsent from "@/components/CookieConsent";

/* -------------------- Suspense fallbacks -------------------- */
function PageFallback() {
  return (
    <div data-testid="page-fallback" className="min-h-screen flex items-center justify-center bg-[#080B12]">
      <div className="flex items-center gap-3 text-slate-400 text-sm">
        <div className="w-6 h-6 rounded-full border-2 border-white/10 border-t-[#6366F1] animate-spin"/>
        <span>Caricamento…</span>
      </div>
    </div>
  );
}

/* -------------------- Prefetch after idle -------------------- */
function usePrefetchApp() {
  useEffect(() => {
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500));
    const cancel = idle(() => {
      // Warm the biggest shared chunks the user will likely hit next.
      import("@/pages/Dashboard").catch(() => {});
      import("@/pages/MyBooks").catch(() => {});
    });
    return () => {
      if (window.cancelIdleCallback) window.cancelIdleCallback(cancel);
    };
  }, []);
}

/* -------------------- Router -------------------- */
function AppRouter() {
  const loc = useLocation();
  usePrefetchApp();
  if (loc.hash?.includes("session_id=")) {
    return (
      <Suspense fallback={<PageFallback/>}>
        <AuthCallback/>
      </Suspense>
    );
  }
  return (
    <Routes>
      {/* Eager: public hero + auth forms render on first paint */}
      <Route path="/"         element={<Landing/>}/>
      <Route path="/login"    element={<Login/>}/>
      <Route path="/register" element={<Register/>}/>
      <Route path="/support"  element={<Suspense fallback={<PageFallback/>}><Support/></Suspense>}/>

      {/* Shop vetrina (pubblica, Stripe checkout) */}
      <Route path="/shop"           element={<Suspense fallback={<PageFallback/>}><Shop/></Suspense>}/>
      <Route path="/shop/terms"     element={<Suspense fallback={<PageFallback/>}><ShopTerms/></Suspense>}/>
      <Route path="/shop/success"   element={<Suspense fallback={<PageFallback/>}><ShopSuccess/></Suspense>}/>
      <Route path="/shop/:slug"     element={<Suspense fallback={<PageFallback/>}><ShopDetail/></Suspense>}/>
      <Route path="/verify-email"   element={<Suspense fallback={<PageFallback/>}><VerifyEmail/></Suspense>}/>
      <Route path="/mini/upgrade/:project_id" element={<Protected><Suspense fallback={<PageFallback/>}><MiniUpgrade/></Suspense></Protected>}/>

      {/* Lazy routes wrapped in Suspense */}
      <Route path="/onboarding"        element={<Suspense fallback={<PageFallback/>}><Protected><Onboarding/></Protected></Suspense>}/>
      <Route path="/auth/callback"     element={<Suspense fallback={<PageFallback/>}><AuthCallback/></Suspense>}/>

      <Route path="/app"                 element={<Suspense fallback={<PageFallback/>}><Protected><Dashboard/></Protected></Suspense>}/>
      <Route path="/app/books"           element={<Suspense fallback={<PageFallback/>}><Protected><MyBooks/></Protected></Suspense>}/>
      <Route path="/app/books/:id"       element={<Suspense fallback={<PageFallback/>}><Protected><Editor/></Protected></Suspense>}/>
      <Route path="/app/new"             element={<Suspense fallback={<PageFallback/>}><Protected><NewEbook/></Protected></Suspense>}/>
      <Route path="/app/billing"         element={<Suspense fallback={<PageFallback/>}><Protected><Billing/></Protected></Suspense>}/>
      <Route path="/app/billing/success" element={<Suspense fallback={<PageFallback/>}><Protected><Billing/></Protected></Suspense>}/>
      <Route path="/app/settings"        element={<Suspense fallback={<PageFallback/>}><Protected><Settings/></Protected></Suspense>}/>
      <Route path="/app/usage"           element={<Suspense fallback={<PageFallback/>}><Protected><Usage/></Protected></Suspense>}/>
      <Route path="/app/exports"         element={<Suspense fallback={<PageFallback/>}><Protected><Exports/></Protected></Suspense>}/>
      <Route path="/app/help"            element={<Suspense fallback={<PageFallback/>}><Protected><Help/></Protected></Suspense>}/>
      <Route path="/app/covers"          element={<Suspense fallback={<PageFallback/>}><Protected><MediaLibrary/></Protected></Suspense>}/>
      <Route path="/app/templates"       element={<Suspense fallback={<PageFallback/>}><Protected><ComingSoon title="Template" desc="Layout professionali pronti all'uso"/></Protected></Suspense>}/>
      <Route path="/app/tools"           element={<Suspense fallback={<PageFallback/>}><Protected><AITools/></Protected></Suspense>}/>
      <Route path="/app/brand"           element={<Suspense fallback={<PageFallback/>}><Protected><ComingSoon title="Brand Kit" desc="Logo, colori, font, tono voce"/></Protected></Suspense>}/>
      <Route path="/app/admin"           element={<Suspense fallback={<PageFallback/>}><Protected><Admin/></Protected></Suspense>}/>

      {/* Admin section (shared chunk) */}
      <Route path="/admin" element={<Suspense fallback={<PageFallback/>}><AdminLayout/></Suspense>}>
        <Route index               element={<Suspense fallback={<PageFallback/>}><AdminOverview/></Suspense>}/>
        <Route path="users"        element={<Suspense fallback={<PageFallback/>}><AdminUsers/></Suspense>}/>
        <Route path="users/:id"    element={<Suspense fallback={<PageFallback/>}><AdminUserDetail/></Suspense>}/>
        <Route path="online"       element={<Suspense fallback={<PageFallback/>}><AdminOnline/></Suspense>}/>
        <Route path="analytics"    element={<Suspense fallback={<PageFallback/>}><AdminAnalytics/></Suspense>}/>
        <Route path="subscriptions"element={<Suspense fallback={<PageFallback/>}><AdminSubscriptions/></Suspense>}/>
        <Route path="revenue"      element={<Suspense fallback={<PageFallback/>}><AdminRevenue/></Suspense>}/>
        <Route path="ai-costs"     element={<Suspense fallback={<PageFallback/>}><AdminAICosts/></Suspense>}/>
        <Route path="ai-usage"     element={<Suspense fallback={<PageFallback/>}><AdminAIUsage/></Suspense>}/>
        <Route path="ebooks"       element={<Suspense fallback={<PageFallback/>}><AdminEbooks/></Suspense>}/>
        <Route path="shop"         element={<Suspense fallback={<PageFallback/>}><AdminShop/></Suspense>}/>
        <Route path="shop/orders"  element={<Suspense fallback={<PageFallback/>}><AdminShopOrders/></Suspense>}/>
        <Route path="audit"        element={<Suspense fallback={<PageFallback/>}><AdminAudit/></Suspense>}/>
        <Route path="errors"       element={<Suspense fallback={<PageFallback/>}><AdminErrors/></Suspense>}/>
      </Route>
      <Route path="/dashboard" element={<Navigate to="/app" replace/>}/>
      <Route path="*" element={<Navigate to={localStorage.getItem("bf_token") ? "/app" : "/"} replace/>}/>
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <FeatureProvider>
        <BrowserRouter>
          <AppRouter/>
          <CreditsGuard/>
          <ServiceDownBanner/>
          <CookieConsent/>
          <Suspense fallback={null}><WhatsAppWidget/></Suspense>
          <Toaster position="top-right" theme="dark" richColors/>
        </BrowserRouter>
      </FeatureProvider>
    </AuthProvider>
  );
}
export default App;
